import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

const TOKEN_KEY   = 'triadha_token';
const REFRESH_KEY = 'triadha_refresh';
const USER_KEY    = 'triadha_user';

/* Access tokens last an hour in production; a player can sit at a table for a
 * good deal longer than that. Refreshing a few minutes early means a token is
 * never presented after it has expired, so a hand is never interrupted by a
 * reconnect that fails. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [wallet,  setWallet]  = useState(10000.00);

  const API = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

  /* The token is also read by callbacks that must not be re-created when it
   * changes — the socket's auth payload among them. A ref keeps those stable
   * while still seeing the current value. */
  const tokenRef = useRef(null);
  const refreshTimer = useRef(null);

  const applyToken = useCallback((accessToken, refreshToken) => {
    tokenRef.current = accessToken;
    setToken(accessToken);
    if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
    else localStorage.removeItem(TOKEN_KEY);

    // A refresh response carries no new refresh token — the original stands.
    if (refreshToken !== undefined) {
      if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
      else localStorage.removeItem(REFRESH_KEY);
    }
  }, []);

  /* One place where every request to our API goes out, so the Authorization
   * header and the error shape are not re-invented per call site. The server
   * answers failures as { message, code }; `code` is what the UI switches on
   * and is attached to the thrown error. */
  const request = useCallback(async (path, { method = 'GET', body, auth = false } = {}) => {
    const headers = {};
    if (body) headers['Content-Type'] = 'application/json';
    if (auth && tokenRef.current) headers.Authorization = `Bearer ${tokenRef.current}`;

    const res = await fetch(`${API}${path}`, {
      method,
      headers,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    // 204 and empty bodies are valid; don't make callers guard for it.
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};

    if (!res.ok) {
      const err = new Error(data.message || 'Something went wrong');
      err.code = data.code;
      err.status = res.status;
      // Carried by new_password_required, for an admin-created account signing
      // in with a temporary password.
      if (data.session) err.session = data.session;
      throw err;
    }
    return data;
  }, [API]);

  const clearSession = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
    applyToken(null, null);
    setUser(null);
    setWallet(10000);
    localStorage.removeItem(USER_KEY);
  }, [applyToken]);

  /* Trades the refresh token for a fresh access token and schedules the next
   * one. The local development provider issues no refresh token, so this is a
   * no-op there and the 24h access token stands on its own. */
  const refreshSession = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) return false;

    try {
      const data = await request('/auth/refresh', {
        method: 'POST',
        body: { refreshToken },
      });
      applyToken(data.accessToken);
      scheduleRefresh(data.expiresIn);
      return true;
    } catch {
      // The refresh token has expired or been revoked: sign out rather than
      // leaving the app in a half-authenticated state.
      clearSession();
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, applyToken, clearSession]);

  /* Defined as a ref-held function because refreshSession and scheduleRefresh
   * refer to each other. */
  const scheduleRefreshRef = useRef(() => {});
  scheduleRefreshRef.current = (expiresInSeconds) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    if (!expiresInSeconds || !localStorage.getItem(REFRESH_KEY)) return;

    const delay = Math.max(expiresInSeconds * 1000 - REFRESH_MARGIN_MS, 30_000);
    refreshTimer.current = setTimeout(() => { refreshSession(); }, delay);
  };
  function scheduleRefresh(expiresInSeconds) {
    scheduleRefreshRef.current(expiresInSeconds);
  }

  const startSession = useCallback((data) => {
    applyToken(data.accessToken, data.refreshToken ?? null);
    setUser(data.user);
    if (data.user?.wallet != null) setWallet(data.user.wallet);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    scheduleRefresh(data.expiresIn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyToken]);

  /* ── Wallet ─────────────────────────────────────────────────────────────────
   *
   * Display only. The browser does not compute or persist this: buying in,
   * leaving a table and topping up all move real money, and the server is the
   * thing that decides by how much. It pushes the new balance over the socket
   * as `wallet:update` and this just records what it was told.
   *
   * This used to apply a delta locally AND POST the same delta back, on top of
   * the write the server had already done for itself — so leaving a table paid
   * out twice. Nothing here should ever write a wallet again; the one
   * remaining caller of POST /api/auth/wallet is the admin page, where an
   * operator is deliberately adjusting somebody's balance.
   */
  const setWalletFromServer = useCallback((value) => {
    if (typeof value === 'number' && Number.isFinite(value)) setWallet(value);
  }, []);

  // ── Restore on load ─────────────────────────────────────────────────────────

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser  = localStorage.getItem(USER_KEY);

    if (!storedToken || !storedUser) { setLoading(false); return; }

    tokenRef.current = storedToken;
    setToken(storedToken);
    try { setUser(JSON.parse(storedUser)); } catch { /* corrupt — /me decides */ }

    /* Confirm the stored token is still good and refresh the wallet. A 401
     * here means the token expired while the tab was closed, so try the
     * refresh token before giving up on the session. */
    request('/auth/me', { auth: true })
      .then(({ user: fresh }) => {
        setUser(fresh);
        if (fresh?.wallet != null) setWallet(fresh.wallet);
        localStorage.setItem(USER_KEY, JSON.stringify(fresh));
      })
      .catch(async (err) => {
        if (err.status !== 401) return;
        if (await refreshSession()) {
          const { user: fresh } = await request('/auth/me', { auth: true });
          setUser(fresh);
          if (fresh?.wallet != null) setWallet(fresh.wallet);
        }
      })
      .finally(() => setLoading(false));

    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Flows ───────────────────────────────────────────────────────────────────

  const login = useCallback(async (identifier, password) => {
    const data = await request('/auth/login', {
      method: 'POST',
      body: { identifier, password },
    });
    startSession(data);
    return data;
  }, [request, startSession]);

  const signUp = useCallback((email, password, name) =>
    request('/auth/signup', { method: 'POST', body: { email, password, name } }),
  [request]);

  const confirmSignUp = useCallback((email, code) =>
    request('/auth/confirm', { method: 'POST', body: { email, code } }),
  [request]);

  const resendCode = useCallback((email) =>
    request('/auth/resend', { method: 'POST', body: { email } }),
  [request]);

  const forgotPassword = useCallback((email) =>
    request('/auth/forgot-password', { method: 'POST', body: { email } }),
  [request]);

  const resetPassword = useCallback((email, code, password) =>
    request('/auth/reset-password', { method: 'POST', body: { email, code, password } }),
  [request]);

  const changePassword = useCallback((oldPassword, newPassword) =>
    request('/auth/change-password', {
      method: 'POST',
      body: { oldPassword, newPassword },
      auth: true,
    }),
  [request]);

  /* Completes a NEW_PASSWORD_REQUIRED challenge: an account created by an
   * admin signing in with its temporary password. Returns a full session. */
  const completeNewPassword = useCallback(async (email, session, password) => {
    const data = await request('/auth/new-password', {
      method: 'POST',
      body: { email, session, password },
    });
    startSession(data);
    return data;
  }, [request, startSession]);

  const logout = useCallback(async () => {
    try {
      await request('/auth/logout', { method: 'POST', auth: true });
    } catch { /* fire and forget — the local session goes either way */ }
    clearSession();
  }, [request, clearSession]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, wallet, setWalletFromServer,
      login, logout, signUp, confirmSignUp, resendCode,
      forgotPassword, resetPassword, changePassword, completeNewPassword,
      refreshSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
